import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { productModules } from '../../data/products';
import { HolderPairIllustration, InsertPairIllustration, ServiceItemsIllustration, StarterKitIllustration } from '../illustrations/TechnicalIllustrations';
function ModuleLink({
  label,
  href
}: {
  label: string;
  href: string;
}) {
  return <Link to={href} data-analytics="product-category-click" data-analytics-label={label} className="mt-auto inline-flex min-h-[44px] items-center gap-2 self-start border-b border-copper pt-8 text-[14px] font-semibold text-navy transition-colors duration-150 ease-precise hover:text-copper" data-id="element-277" data-mp="tvvelrrb070l">
      {label}
      <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-278" data-mp="2eb6u0ffb71ib" />
    </Link>;
}
function ModuleFrame({
  index,
  heading,
  illustration,
  children,
  className = ''
}: {
  index: string;
  heading: string;
  illustration: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return <article className={`relative flex flex-col overflow-hidden border border-rule bg-white p-6 lg:p-9 ${className}`} data-id="element-279" data-mp="1m4b7oor6hd2u">
      <div className="relative flex items-center gap-3 border-b border-rule pb-4 sm:gap-5" data-id="element-280" data-mp="1q77uysrnxxd4">
        <h3 className="min-w-0 flex-1 font-display text-[22px] font-medium leading-tight text-navy lg:text-[26px]" data-id="element-281" data-mp="1v5hsha0v1a1w">
          {heading}
        </h3>
        <div className="w-[72px] shrink-0 overflow-hidden sm:w-[104px] lg:w-[120px]" data-id="element-282" data-mp="ycw3eqfucloy">
          {illustration}
        </div>
        <span className="hidden shrink-0 font-mono text-[10px] tracking-label text-copper sm:block" data-id="element-283" data-mp="17fx8cnsz06r1">
          {index}
        </span>
        <span className="absolute bottom-0 right-0 translate-y-1/2 bg-white pl-1.5 font-mono text-[10px] leading-none tracking-label text-copper sm:hidden" data-id="element-284" data-mp="1c7f672zp5r7s">
          {index}
        </span>
      </div>
      {children}
    </article>;
}
function SpecRow({
  term,
  value,
  emphasis = false
}: {
  term: string;
  value: string;
  emphasis?: boolean;
}) {
  return <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3" data-id="element-285" data-mp="1i25dg9ivfd46">
      <dt className={`font-mono ${emphasis ? 'text-[14px] text-navy' : 'text-[13.5px] text-navy'}`} data-id="element-286" data-mp="1vtahdnt9urkb">
        {term}
      </dt>
      <dd className="rp-tabular font-mono text-[12.5px] tracking-[0.04em] text-graphite/70" data-id="element-287" data-mp="1rs8fez7z01en">
        {value}
      </dd>
    </div>;
}
export function ProductSystem() {
  const [kits, holders, inserts, accessories] = productModules;
  return <section id="products" aria-labelledby="products-heading" className="w-full bg-warm" data-id="element-288" data-mp="1d2ki092wtmw7">
      <div className="mx-auto max-w-shell px-5 py-16 lg:px-10 lg:py-24" data-id="element-289" data-mp="183g81g85vl4t">
        <SectionHeading id="products-heading" eyebrow="PRODUCT SYSTEM" heading="Build the Shear Geometry setup your application needs" intro="Start with the shank size, then review the published roughing, chipbreaker or titanium-oriented insert. Part numbers are from the Shear Geometry catalog. Call 800-397-7745 for pricing and availability." data-id="element-290" data-mp="2ohz9wyg9hokg" />

        <div className="mt-14 grid gap-5 lg:grid-cols-12" data-id="element-291" data-mp="2i1peo8v60u1y">
          {/* A — Starter kits */}
          <ModuleFrame index={kits.index} heading={kits.heading} illustration={<StarterKitIllustration data-id="element-293" data-mp="3aqei4ejp1bgv" />} className="lg:col-span-7" data-id="element-292" data-mp="2i422h2nltymx">
            <p className="relative mt-6 max-w-[52ch] text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-294" data-mp="3asqy4m5w5bra">
              {kits.lead}
            </p>
            <dl className="mt-7 grid gap-px bg-rule sm:grid-cols-2" data-id="element-295" data-mp="311d2wqkz2y3y">
              <div className="bg-white p-5" data-id="element-296" data-mp="35zoasvrpiyjj">
                <dt className="font-mono text-[15px] text-navy" data-id="element-297" data-mp="35yd9d2lqi2vl">SG Kit 1″</dt>
                <dd className="rp-tabular mt-2 font-mono text-[11px] tracking-label text-graphite/70" data-id="element-298" data-mp="3toz4xuejxuop">
                  1-INCH HOLDER SYSTEM
                </dd>
              </div>
              <div className="bg-white p-5" data-id="element-299" data-mp="29twh3yny1pjx">
                <dt className="font-mono text-[15px] text-navy" data-id="element-300" data-mp="29vplf7b33xor">SG Kit 3/4″</dt>
                <dd className="rp-tabular mt-2 font-mono text-[11px] tracking-label text-graphite/70" data-id="element-301" data-mp="25rhud3h5m5nd">
                  3/4-INCH HOLDER SYSTEM
                </dd>
              </div>
            </dl>
            <p className="mt-6 max-w-[58ch] border-l-2 border-copper pl-4 text-[14px] leading-[1.65] text-graphite/85" data-id="element-302" data-mp="1vveu635v4h32">
              {kits.details[2]}
            </p>
            <ModuleLink label={kits.linkLabel} href={kits.href} data-id="element-303" data-mp="2ojaq35nly4ys" />
          </ModuleFrame>

          {/* B — Tool holders */}
          <ModuleFrame index={holders.index} heading={holders.heading} illustration={<HolderPairIllustration data-id="element-305" data-mp="2esg13tiz8r9n" />} className="lg:col-span-5" data-id="element-304" data-mp="2jrahypxdw5r4">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-306" data-mp="3c9oiwye0o7uf">
              {holders.lead}
            </p>
            <dl className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-307" data-mp="37gbyl0gden7h">
              <SpecRow term="SALXR 16-3D" value="1-INCH SHANK" emphasis data-id="element-308" data-mp="3eu4d8url87h7" />
              <SpecRow term="SALXR 12-3B" value="3/4-INCH SHANK" emphasis data-id="element-309" data-mp="3jrmdlwrjhedl" />
            </dl>
            <p className="mt-4 text-[14px] leading-[1.6] text-graphite/80" data-id="element-310" data-mp="3oriirzbz3buq">
              Uses low-profile insert screws and a T-15 Torx driver.
            </p>
            <ModuleLink label={holders.linkLabel} href={holders.href} data-id="element-311" data-mp="3trn2rzc3aopp" />
          </ModuleFrame>

          {/* C — Inserts & chipbreakers */}
          <ModuleFrame index={inserts.index} heading={inserts.heading} illustration={<InsertPairIllustration data-id="element-313" data-mp="bbsztpoleh08" />} className="lg:col-span-8" data-id="element-312" data-mp="6lcku154inbr">
            <p className="mt-6 max-w-[62ch] text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-314" data-mp="gb5rqoubme9o">
              {inserts.lead}
            </p>
            <div className="mt-7 border-y border-rule" data-id="element-315" data-mp="lbthl76tcp2b">
              <div className="hidden items-baseline justify-between border-b border-rule pb-2 sm:flex" data-id="element-316" data-mp="pbu83q76sqpq">
                <span className="font-mono text-[9.5px] tracking-label text-copper" data-id="element-317" data-mp="2mc886u746vdd">
                  PUBLISHED INSERT
                </span>
                <span className="font-mono text-[9.5px] tracking-label text-copper" data-id="element-318" data-mp="2hf83amm03ynu">
                  NOSE RADIUS
                </span>
              </div>
              <ul className="divide-y divide-rule" data-id="element-319" data-mp="2qdth64gj2pia">
                {inserts.details.map(sku => {
                const [name, radius] = sku.split(' — ');
                return <li key={sku} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-2.5" data-id="element-320" data-mp="304en6cyj6wb8">
                      <span className="font-mono text-[13px] leading-snug text-navy" data-id="element-321" data-mp="3e1l3m0vs8ixa">
                        {name}
                      </span>
                      <span className="rp-tabular font-mono text-[12.5px] text-graphite/70" data-id="element-322" data-mp="1jqxy5yqx4usi">
                        {radius}
                      </span>
                    </li>;
              })}
              </ul>
            </div>
            <p className="mt-4 max-w-[62ch] text-[13.5px] leading-[1.6] text-graphite/70" data-id="element-323" data-mp="15mqmgn9227db">
              {inserts.note}
            </p>
            <ModuleLink label={inserts.linkLabel} href={inserts.href} data-id="element-324" data-mp="vvcquqk85rpg" />
          </ModuleFrame>

          {/* D — Accessories */}
          <ModuleFrame index={accessories.index} heading={accessories.heading} illustration={<ServiceItemsIllustration data-id="element-326" data-mp="lyhuad7ii1dt" />} className="lg:col-span-4" data-id="element-325" data-mp="10s35d9gdsf8r">
            <p className="mt-6 text-[15.5px] leading-[1.65] text-graphite/85" data-id="element-327" data-mp="qwrpgnt8l1zf">
              {accessories.lead}
            </p>
            <ul className="mt-6 divide-y divide-rule border-y border-rule" data-id="element-328" data-mp="hus8fvi81xwh">
              {accessories.details.map(item => <li key={item} className="py-3.5 font-mono text-[13.5px] text-navy" data-id="element-329" data-mp="279gihq3cb3rc">
                  {item}
                </li>)}
            </ul>
            <ModuleLink label={accessories.linkLabel} href={accessories.href} data-id="element-330" data-mp="3krxbbiqbtxfk" />
          </ModuleFrame>
        </div>
      </div>
    </section>;
}